/**
 * autoClassify — salvaged 2-pass document auto-classifier.
 *
 * Lifted out of the now-deleted `UnifiedCaptureSurface.handleCapture` (Phase
 * G.1, slot-1 T0 salvage) so the canonical capture flow keeps Bill's
 * "drop any document → system figures out what it is" behaviour after the dead
 * unified surface is removed. Reuses the doc-type mappers in `captureRouting`.
 *
 *   Pass 1 — Azure DI prebuilt-idDocument (US DL / State ID / US Passport) via
 *            IdExtractionService → azureDiTypeToDocTypeValue.
 *   Pass 2 — /v3/nra/classify (foreign passport / visa / I-94 / I-20 / DS-2019)
 *            when Pass 1 returns nothing or maps to "other".
 *
 * Whichever finds a confident type wins; otherwise returns 'other' so the FSR
 * picks from the override dropdown. Never throws — classification is best-
 * effort and must not block capture.
 */

import type { ApiClient } from '@retail-ops/api-client';
import { IdExtractionService, type IdExtractionContext } from '../../../services/IdExtractionService.js';
import {
  azureDiTypeToDocTypeValue,
  docTypeBranding,
  nraClassToDocTypeValue,
} from '../individual/captureRouting.js';

export interface AutoClassifyResult {
  /** Best-guess doc-type value (a DOC_TYPE_OPTIONS.value, or 'other'). */
  docTypeValue: string;
  /**
   * Officer-facing "detected as" label. Bill 2026-05-28 — was the raw Azure
   * DI string (e.g. "idDocument.usSocialSecurityCard") which is ugly + leaks
   * vendor namespace. Now resolved to the matched DOC_TYPE_BRANDING label
   * (e.g. "SSN Card"), falling back to the raw string only when no friendly
   * label exists (truly unrecognized).
   */
  detectedRawType?: string;
}

/**
 * Run the 2-pass classifier against captured bytes. `base64` is the raw image/
 * PDF payload (no data: prefix); `mimeType` its MIME. Returns the best guess +
 * a human "detected as" label. Safe to call on every capture.
 */
export async function autoClassifyCapture(
  base64: string,
  mimeType: string,
  apiClient: ApiClient,
  context?: IdExtractionContext,
): Promise<AutoClassifyResult> {
  let guessed = 'other';
  let detectedRawType: string | undefined;

  // -- Pass 1: prebuilt-idDocument (US photo IDs) --
  try {
    const svc = new IdExtractionService(apiClient);
    // Cart linkage (Bill 2026-10-16): this is usually the FIRST Azure DI call on the
    // bytes, so it is the one that writes the dbo.DocumentExtract row — it must carry
    // the cart id (the officer code is stamped server-side from the bearer).
    const ex = await svc.extractFromImage(base64, mimeType, undefined, context);
    guessed = azureDiTypeToDocTypeValue(ex.rawDocumentType);
    if (ex.rawDocumentType) {
      // Friendly label preferred; fall back to raw when no mapping.
      const branding = docTypeBranding(guessed);
      detectedRawType = branding?.label ?? ex.rawDocumentType;
    }
  } catch {
    // Pass 1 didn't recognize — fall through to Pass 2.
  }

  // -- Pass 2: NRA classifier (only when Pass 1 didn't land on a US ID) --
  if (guessed === 'other') {
    try {
      const cls = await apiClient.retail.nra.classify({ image: base64, mimeType });
      if (cls && cls.success && cls.isConfident) {
        const nraGuess = nraClassToDocTypeValue(cls.topClass);
        if (nraGuess !== 'other') {
          guessed = nraGuess;
          // Friendly label + confidence percentage when we have a brand match.
          const branding = docTypeBranding(guessed);
          const friendly = branding?.label ?? String(cls.topClass);
          const pct = typeof cls.topConfidence === 'number' ? (cls.topConfidence * 100).toFixed(0) : '';
          detectedRawType = pct ? `${friendly} (${pct}%)` : friendly;
        }
      }
    } catch {
      // Both probes failed — officer picks from the dropdown.
    }
  }

  return { docTypeValue: guessed, ...(detectedRawType ? { detectedRawType } : {}) };
}
